import _, {noop} from 'lodash';
import React, {ReactNode, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {
  Handler,
  HandlerOf,
  Renderer,
  RendererOf,
} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {ScrollDirectionsEnum, ScrollerMoreStatuses} from '../../../../srcCommon/scroll/scrollerTypes';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {AsyncMoreStatusesEnum} from '../../../store/appTypes';
import {VirtualScroller} from '../scroll/virtualScroller';
import {
  firstElementPaddingLeft,
  virtualTableHeaderRowHeight,
  virtualTableRowCheckboxWrapperWidth,
} from './helpers/tableHelpers';
import {SelectedColumnSort, SortableTableColumn, TableColumn} from './tableColumn';
import {TableHeaderRow} from './tableHeaderRow';
import {TableLoadingRows} from './tableLoadingRows';
import {TableRowRenderOptions} from './tableRow';

/*
 * Constants.
 */

const virtualScrollerDefaultPaddingBottom = 48;
/** Currently medium is not used. */
const rowHeightsFromVisualSizes = makeSizeConstants(40, 56, 56);
const totalLoadingRowsWithNoData = 6;
const totalLoadingRowsWithData = 3;
const errorRowHeight = 250;

/*
 * Types.
 */

export type VirtualTableRowSiblingData<T> = {
  previous?: T;
  next?: T;
};

export type VirtualTableRowRenderer<T> = (
  data: T,
  options: TableRowRenderOptions,
  siblingData?: VirtualTableRowSiblingData<T>,
) => ReactNode;

export type RenderMultiSelectHeader = (onMultiSelectHeaderChange: HandlerOf<boolean>) => ReactNode;

export interface VirtualTableInheritableProps<T> {
  columns: ReadonlyArray<TableColumn<unknown>>;

  sortedColumn?: SelectedColumnSort;
  sortableColumns?: ReadonlyArray<SortableTableColumn>;
  items?: ReadonlyArray<T>;
  size?: VisualSizesEnum.SMALL | VisualSizesEnum.LARGE;

  shouldAllowMultiSelect?: boolean;
  isInMultiSelectMode?: boolean;
  renderRow: VirtualTableRowRenderer<T>;
  renderEmpty?: Renderer;
  renderMultiSelectHeader?: RenderMultiSelectHeader;
  onMultiSelectHeaderChange?: HandlerOf<boolean>;

  bottomPadding?: number;
}

type RenderVirtualTableHeaderOptions = {
  columns: ReadonlyArray<TableColumn<unknown>>;
  sortableColumns?: ReadonlyArray<SortableTableColumn>;
  sortedColumn?: SelectedColumnSort;
  renderOptions: TableRowRenderOptions;
  shouldRenderEmptyState: boolean;
  isInMultiSelectMode?: boolean;

  renderMultiSelectHeader?: RenderMultiSelectHeader;
  updateSort: HandlerOf<SelectedColumnSort>;
  onMultiSelectHeaderChange?: HandlerOf<boolean>;
};

interface VirtualTableBaseProps<T> extends VirtualTableInheritableProps<T> {
  shouldRenderEmptyState: boolean;
  onSortUpdate?: HandlerOf<SelectedColumnSort>;
  computeMoreStatuses: () => ScrollerMoreStatuses;
  onLoadMore: Handler;
  hasData: boolean;
  className?: string;
  isFetching?: boolean;
  hasErredFetching?: boolean;
  renderErrorFetchingState?: Renderer;
}

/*
 * Styles.
 */

const StyledTableWrapperDiv = styled.div`
  width: 100%;
  height: 100%;
`;

const StyledErrorTableWrapperDiv = styled(StyledTableWrapperDiv)`
  overflow: hidden;
  // We do not want any pointer events such as scrolling, etc.
  pointer-events: none;
`;

/*
 * Component.
 */

export const VirtualTableBase = <T extends {}>(props: VirtualTableBaseProps<T>) => {
  const {
    columns,
    sortedColumn,
    sortableColumns,
    items,
    size,
    shouldAllowMultiSelect,
    isInMultiSelectMode,
    renderRow,
    renderEmpty,
    renderMultiSelectHeader,
    onMultiSelectHeaderChange,
    bottomPadding,
    shouldRenderEmptyState,
    computeMoreStatuses,
    onLoadMore,
    hasData,
    className,
    hasErredFetching,
    isFetching,
    renderErrorFetchingState,
  } = props;

  const totalLoadingRows = useMemo(
    () => (hasData ? totalLoadingRowsWithData : totalLoadingRowsWithNoData),
    [hasData],
  );
  const tableRowHeight = rowHeightsFromVisualSizes[size ?? VisualSizesEnum.LARGE];
  const [shouldShowRightGradient, setShouldShowRightGradient] = useState(false);
  const [shouldShowStickyGradient, setShouldShowStickyGradient] = useState(false);
  const [tableWrapperRef, {width: tableWrapperWidth}] = useMeasureElement();
  const tableWidth = useMemo(() => {
    // Calculate the widths from the column settings.
    const columnsTotalWidth = _(columns)
      .map(
        (col) =>
          col.settings?.cellWidth?.maxWidth ??
          col.settings?.cellWidth?.width ??
          col.settings?.cellWidth?.minWidth,
      )
      .compact()
      .sum();
    // Add additional widths that need to be taken in to account.
    const additionalWidth =
      (shouldAllowMultiSelect ? virtualTableRowCheckboxWrapperWidth : 0) + firstElementPaddingLeft;
    // If we are greater than the wrapper width, we should pass back a width.
    if (columnsTotalWidth > tableWrapperWidth + additionalWidth) {
      return columnsTotalWidth + additionalWidth;
    }
    return undefined;
  }, [tableWrapperWidth, columns, shouldAllowMultiSelect]);

  useEffect(() => {
    setShouldShowRightGradient(Boolean(tableWidth));
  }, [tableWidth]);

  const updateSort: HandlerOf<SelectedColumnSort> = (sort) => {
    props.onSortUpdate?.(sort);
  };

  const renderOptions: TableRowRenderOptions = {
    shouldShowStickyGradient,
    shouldShowRightGradient,
    shouldAllowMultiSelect,
    tableRowHeight,
    size,
  };

  const renderItem: RendererOf<T> = (id) => {
    const selectedData = getSelectedItemFromId<T>(id, items);
    if (!selectedData) {
      return null;
    }

    const siblingData = shouldAllowMultiSelect
      ? {
          previous: getPreviousSiblingItemId<T>(id, items),
          next: getNextSiblingItemId<T>(id, items),
        }
      : undefined;

    return renderRow(selectedData, renderOptions, siblingData);
  };

  const renderSectionHeader = () =>
    renderVirtualTableHeader({
      columns,
      sortableColumns,
      sortedColumn,
      shouldRenderEmptyState,
      renderOptions,
      isInMultiSelectMode,
      updateSort,
      renderMultiSelectHeader,
      onMultiSelectHeaderChange,
    });

  // If we erred during the fetch, we need to render an error state.
  if (!isFetching && hasErredFetching && renderErrorFetchingState) {
    return (
      <StyledErrorTableWrapperDiv>
        <VirtualScroller
          padding={{
            bottom: bottomPadding || virtualScrollerDefaultPaddingBottom,
          }}
          itemHeight={errorRowHeight}
          itemIds={[[1]]}
          moreStatuses={{
            [ScrollDirectionsEnum.TOP]: AsyncMoreStatusesEnum.ALL_LOADED,
            [ScrollDirectionsEnum.BOTTOM]: AsyncMoreStatusesEnum.ALL_LOADED,
          }}
          loaderHeight={totalLoadingRows * tableRowHeight}
          onLoadMore={noop}
          renderItem={renderErrorFetchingState}
          renderLoader={() => null}
          sectionHeaderHeight={virtualTableHeaderRowHeight}
          renderSectionHeader={renderSectionHeader}
        />
      </StyledErrorTableWrapperDiv>
    );
  }

  return (
    <StyledTableWrapperDiv ref={tableWrapperRef} className={className}>
      <VirtualScroller<T>
        padding={{
          bottom: bottomPadding || virtualScrollerDefaultPaddingBottom,
        }}
        width={tableWidth}
        itemHeight={tableRowHeight}
        itemIds={items ? [items] : [[]]}
        moreStatuses={computeMoreStatuses()}
        loaderHeight={totalLoadingRows * tableRowHeight}
        onLoadMore={onLoadMore}
        renderItem={renderItem}
        renderLoader={() => (
          <TableLoadingRows
            columns={columns}
            totalLoadingRows={totalLoadingRows}
            size={size}
            options={renderOptions}
          />
        )}
        sectionHeaderHeight={virtualTableHeaderRowHeight}
        renderSectionHeader={renderSectionHeader}
        onScroll={({scrollLeft, scrollRemainingSpaceRight}) => {
          // If we have space remaining to the right, we should show the gradient
          setShouldShowRightGradient(scrollRemainingSpaceRight > 0);
          // If we have scrolled from the left, we should show sticky gradient
          setShouldShowStickyGradient(scrollLeft > 0);
        }}
      />
      {renderEmpty && shouldRenderEmptyState && renderEmpty()}
    </StyledTableWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderVirtualTableHeader(options: RenderVirtualTableHeaderOptions) {
  const {
    columns,
    sortableColumns,
    sortedColumn,
    shouldRenderEmptyState,
    isInMultiSelectMode,
    renderOptions: {shouldShowStickyGradient, shouldShowRightGradient, shouldAllowMultiSelect},
    updateSort,
    renderMultiSelectHeader,
    onMultiSelectHeaderChange,
  } = options;
  if (
    isInMultiSelectMode &&
    !shouldRenderEmptyState &&
    renderMultiSelectHeader &&
    onMultiSelectHeaderChange
  ) {
    return renderMultiSelectHeader(onMultiSelectHeaderChange);
  }
  return (
    <TableHeaderRow
      columns={columns}
      sortableColumns={!shouldRenderEmptyState ? sortableColumns : undefined}
      sortedColumn={!shouldRenderEmptyState ? sortedColumn : undefined}
      shouldShowStickyGradient={Boolean(shouldShowStickyGradient)}
      shouldShowRightGradient={Boolean(shouldShowRightGradient) && !shouldRenderEmptyState}
      shouldAllowMultiSelect={shouldAllowMultiSelect}
      shouldRenderEmptyState={shouldRenderEmptyState}
      onMultiSelectChange={onMultiSelectHeaderChange}
      onSortChange={updateSort}
    />
  );
}

function getSelectedItemFromId<T>(id: T, items?: ReadonlyArray<T>) {
  if (items) {
    return items.find((d) => d === id);
  }
  return undefined;
}

function getPreviousSiblingItemId<T>(id: T, items?: ReadonlyArray<T>) {
  if (items) {
    const currentIndex = items.indexOf(id);
    // Make sure we are not looking before the array
    if (currentIndex === 0) {
      return undefined;
    }
    return items[currentIndex - 1];
  }
  return undefined;
}

function getNextSiblingItemId<T>(id: T, items?: ReadonlyArray<T>) {
  if (items) {
    const currentIndex = items.indexOf(id);
    // Make sure we are not at the end of the array
    if (currentIndex === items.length - 1) {
      return undefined;
    }
    return items[currentIndex + 1];
  }
  return undefined;
}
